/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * A-3 (S703) — ONE Stripe `<Elements>` context for every address form.
 *
 * B-11 exists because there were THREE of these and only two had been given a
 * language: the cart's billing form, the cart's address block and Account
 * Settings' billing editor each built their own `<Elements>`, and the editor's
 * had no `locale`, so a French customer editing their billing address met
 * Stripe's field labels, its validation text and its test-mode banner in
 * English. Each one was written at a different time and each is defensible on
 * its own; nothing said the language applied to all of them.
 *
 * A fourth form gets the language for free.
 *
 * Two things are deliberately parameters rather than fixed here:
 *
 *   * `appearance` — the cart's address block uses `{ theme: "flat" }` and the
 *     other two use the shop's `stripeAppearance`. Folding that difference away
 *     would be a visual change smuggled into a language fix.
 *   * `stripe` — X-3 (S711): no longer passed by the callers. Each used to keep
 *     its own module-level `loadStripe(...)` promise, and a promise that failed
 *     once was failed for the life of the page. The one owner is now
 *     lib/stripe-loader.ts, which retries a failed load on reconnection.
 *
 * The language is read through `useTranslation()` rather than taken as a prop,
 * so switching language re-renders this component and Stripe rebuilds its
 * context in the new language. It goes through `toStripeLocale()` (N-5), which
 * is what turns the shop's Brazilian `pt` into Stripe's `pt-BR`.
 */

import React from "react";
import { Elements } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import type { Appearance } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { stripeAppearance } from "@/components/cartpage/forms/stripe-appearance";
import { toStripeLocale } from "@/lib/supported-languages";
import { useStripePromise } from "@/hooks/use-stripe-promise";

interface StripeElementsProviderProps {
  /**
   * X-3 (S711 review): normally omitted. The payment library comes from the one
   * owner in lib/stripe-loader.ts, which retries a failed load when the
   * connection returns. Accepted for tests that inject their own.
   */
  stripe?: Promise<Stripe | null>;
  /** Defaults to the shop's own appearance. */
  appearance?: Appearance;
  children: React.ReactNode;
}

/**
 * X-3 (S711 review): a form whose payment library could not be loaded says so
 * and offers a way out, instead of rendering nothing. "Try again" re-requests
 * the library; "Reload page" is the last resort that always works.
 */
export const PaymentFormUnavailable: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
      data-testid="payment-form-unavailable"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold">{t("payment.formUnavailable.title")}</p>
          <p className="mt-0.5 text-[13px] leading-snug text-amber-800">
            {t("payment.formUnavailable.body")}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {t("payment.formUnavailable.retry")}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => window.location.reload()}>
          {t("payment.formUnavailable.reload")}
        </Button>
      </div>
    </div>
  );
};

export const StripeElementsProvider: React.FC<StripeElementsProviderProps> = ({
  stripe,
  appearance = stripeAppearance,
  children,
}) => {
  const { i18n: activeI18n } = useTranslation();
  const shared = useStripePromise();

  if (!stripe && shared.failed) {
    return <PaymentFormUnavailable onRetry={shared.retry} />;
  }

  return (
    <Elements
      // X-3: `<Elements>` refuses a changed `stripe` prop, so a retried load
      // remounts the context under a new key.
      key={stripe ? "injected" : `load-${shared.generation}`}
      stripe={stripe ?? shared.stripe}
      options={{
        // `mode: "setup"` with no amount — Stripe's SDK v6 rejects an amount in
        // setup mode, and every one of these forms collects an address rather
        // than taking a payment.
        mode: "setup",
        currency: "usd",
        appearance,
        locale: toStripeLocale(activeI18n.language),
      }}
    >
      {children}
    </Elements>
  );
};

export default StripeElementsProvider;
